/**
 * Post-process mammoth.convertToHtml() output so an imported Word document
 * renders cleanly in the editor / preview / approved letter.
 *
 * Three normalizations, in order:
 *   1. Merge consecutive sibling `<ol>` / `<ul>` elements. Mammoth often
 *      emits each numbered paragraph as its own single-item list, which
 *      makes every entry restart at "1" instead of 1, 2, 3, ...
 *   2. Strip the legacy HTML `align` attribute from `<table>` and `<img>`.
 *      Word documents very commonly carry `align="left"` on tables; that
 *      attribute floats the table left and lets subsequent paragraph text
 *      wrap to the right of it, ruining the letter layout.
 *   3. Collapse runs of empty paragraphs. Word "signature space" is often
 *      authored as several blank Enter keystrokes, which mammoth faithfully
 *      converts into many `<p></p>` / `<p><br></p>` blocks. Those stack
 *      visible vertical gaps below the signature box; keep one as a
 *      separator and drop the rest.
 */
export function normalizeImportedDocxHtml(html: string): string {
  if (!html) return html;
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    mergeListsIn(doc.body);
    stripFloatingAlign(doc.body);
    collapseEmptyParagraphs(doc.body);
    return doc.body.innerHTML;
  } catch {
    return html;
  }
}

function isListTag(node: Node | null | undefined): node is HTMLElement {
  return Boolean(
    node &&
      node.nodeType === Node.ELEMENT_NODE &&
      ((node as Element).tagName === 'OL' || (node as Element).tagName === 'UL'),
  );
}

function isIgnorableWhitespace(node: Node | null | undefined): boolean {
  return Boolean(
    node &&
      node.nodeType === Node.TEXT_NODE &&
      !(node.textContent || '').trim(),
  );
}

function mergeListsIn(root: Element): void {
  // First recurse into element children so nested lists are merged before
  // we look at the parent's sibling chain.
  Array.from(root.children).forEach((child) => mergeListsIn(child));

  let cursor: Node | null = root.firstChild;
  while (cursor) {
    if (!isListTag(cursor)) {
      cursor = cursor.nextSibling;
      continue;
    }
    const leadList = cursor;
    const targetTag = leadList.tagName;
    let next = leadList.nextSibling;
    while (next) {
      if (isIgnorableWhitespace(next)) {
        const toRemove = next;
        next = next.nextSibling;
        toRemove.parentNode?.removeChild(toRemove);
        continue;
      }
      if (
        isListTag(next) &&
        (next as Element).tagName === targetTag &&
        // Lists rebuilt from Word copy-paste carry their own numbering style
        // and an explicit start; never merge those into a different list
        // (e.g. "1. 2. 3." followed by "a) b)" must not become 1–5).
        !(next as Element).hasAttribute('start') &&
        (next as Element).getAttribute('data-list-style') === leadList.getAttribute('data-list-style')
      ) {
        const sibling = next as HTMLElement;
        // Move every child node (typically <li>) into the lead list.
        while (sibling.firstChild) {
          leadList.appendChild(sibling.firstChild);
        }
        const toRemove = sibling;
        next = sibling.nextSibling;
        toRemove.parentNode?.removeChild(toRemove);
        continue;
      }
      break;
    }
    cursor = leadList.nextSibling;
  }
}

function stripFloatingAlign(root: Element): void {
  root.querySelectorAll('table[align], img[align]').forEach((el) => {
    el.removeAttribute('align');
  });
}

function isParagraphEmpty(el: Element): boolean {
  if (el.tagName !== 'P') return false;
  // A paragraph counts as "empty" if it has no meaningful text and no
  // structural children beyond <br>. This catches <p></p>, <p><br></p>,
  // <p>&nbsp;</p> and Word's <p><span>&nbsp;</span></p> dressed-up blanks.
  if ((el.textContent || '').replace(/\u00a0/g, '').trim() !== '') return false;
  for (const child of Array.from(el.children)) {
    if (child.tagName !== 'BR' && child.tagName !== 'SPAN') return false;
  }
  return true;
}

function collapseEmptyParagraphs(root: Element): void {
  // Walk siblings; whenever we hit an empty <p>, drop every immediately
  // following empty <p> (text-node whitespace between them does not count).
  const children = Array.from(root.children);
  let i = 0;
  while (i < children.length) {
    if (!isParagraphEmpty(children[i])) {
      i += 1;
      continue;
    }
    let j = i + 1;
    while (j < children.length && isParagraphEmpty(children[j])) {
      const toRemove = children[j];
      toRemove.parentNode?.removeChild(toRemove);
      j += 1;
    }
    i = j;
  }
}
